import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    RadioGroup,
    FormControlLabel,
    Radio,
    CircularProgress,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { adminApi } from 'utils/api';

const styles = {
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
    },
    helpText: {
        color: 'text.secondary',
        fontSize: '0.8125rem',
        mb: 2,
    },
    formContainer: {
        bgcolor: 'background.default',
        p: 2,
        borderRadius: 1,
        mb: 3,
    },
    formRow: {
        display: 'flex',
        gap: 2,
        mb: 2,
    },
    formSection: {
        mb: 2,
    },
    sectionTitle: {
        fontSize: '0.875rem',
        fontWeight: 600,
        mb: 1,
    },
    tableContainer: {
        maxHeight: 400,
    },
    repoInfo: {
        fontSize: '0.75rem',
        color: 'text.secondary',
    },
    profileChip: {
        mr: 0.5,
        mb: 0.5,
    },
};

const initialFormState = {
    id: '',
    name: '',
    description: '',
    nodeVersion: '20',
    // 옵션 타입: 'bundle' 또는 'backend'
    optionType: 'bundle',
    // 옵션1(Bundle) 전용
    repoUrl: '',
    // 옵션2(BackEnd) 전용
    buildScript: '',
    branchMode: 'release',
    fixedBranch: '',
    majorVersion: '',
    backendUrl: '',
    backendBranch: 'main',
    frontendUrl: '',
    frontendBranch: 'main',
    convertUrl: '',
    convertBranch: 'main',
};

export default function ProductsTab() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(initialFormState);
    const [submitting, setSubmitting] = useState(false);

    // Profile modal state
    const [profilesModal, setProfilesModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [newProfileName, setNewProfileName] = useState('');
    const [newProfileDesc, setNewProfileDesc] = useState('');

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            const { data } = await adminApi.getProducts();
            setProducts(data.products || []);
        } catch (err) {
            toast.error('제품 목록 로드 실패');
        } finally {
            setLoading(false);
        }
    };

    const handleShowAddForm = () => {
        setForm(initialFormState);
        setEditMode(false);
        setEditId(null);
        setShowForm(true);
    };

    const handleEditProduct = async (productId) => {
        try {
            const { data } = await adminApi.getProduct(productId);
            const p = data.product;

            // optionType 판단: repoUrl이 있으면 bundle, 아니면 backend
            const isBundle = !!p.repoUrl;

            setForm({
                id: p.id,
                name: p.name,
                description: p.description || '',
                nodeVersion: p.nodeVersion || '20',
                optionType: isBundle ? 'bundle' : 'backend',
                repoUrl: p.repoUrl || '',
                buildScript: p.buildScript || '',
                branchMode: p.branchMode || 'release',
                fixedBranch: p.fixedBranch || '',
                majorVersion: p.majorVersion !== null && p.majorVersion !== undefined ? String(p.majorVersion) : '',
                backendUrl: p.repos?.backend?.url || '',
                backendBranch: p.repos?.backend?.branch || 'main',
                frontendUrl: p.repos?.frontend?.url || '',
                frontendBranch: p.repos?.frontend?.branch || 'main',
                convertUrl: p.repos?.convert?.url || '',
                convertBranch: p.repos?.convert?.branch || 'main',
            });
            setEditMode(true);
            setEditId(productId);
            setShowForm(true);
        } catch (err) {
            toast.error('제품 정보 로드 실패');
        }
    };

    const handleSubmitForm = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const productData = {
            id: form.id,
            name: form.name,
            description: form.description,
            nodeVersion: form.nodeVersion,
            optionType: form.optionType,
        };

        if (form.optionType === 'bundle') {
            // Bundle 타입: Repository 경로만
            productData.repoUrl = form.repoUrl;
            productData.buildScript = '';
            productData.branchMode = 'release';
            productData.fixedBranch = '';
            productData.majorVersion = null;
            productData.repos = null;
        } else {
            // BackEnd 타입: 기존 구조
            productData.repoUrl = '';
            productData.buildScript = form.buildScript;
            productData.branchMode = form.branchMode;
            productData.fixedBranch = form.fixedBranch;
            productData.majorVersion = form.branchMode === 'release' && form.majorVersion ? parseInt(form.majorVersion, 10) : null;
            productData.repos = {
                backend: {
                    url: form.backendUrl,
                    branch: form.backendBranch || 'main',
                },
                frontend: form.frontendUrl ? {
                    url: form.frontendUrl,
                    branch: form.frontendBranch || 'main',
                } : null,
                convert: form.convertUrl ? {
                    url: form.convertUrl,
                    branch: form.convertBranch || 'main',
                } : null,
            };
        }

        try {
            if (editMode) {
                await adminApi.updateProduct(editId, productData);
                toast.success('제품이 수정되었습니다.');
            } else {
                await adminApi.createProduct(productData);
                toast.success('제품이 추가되었습니다.');
            }
            setShowForm(false);
            setForm(initialFormState);
            loadProducts();
        } catch (err) {
            toast.error(err.response?.data?.error || '제품 저장 실패');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteProduct = async (productId) => {
        if (!window.confirm('제품을 삭제하시겠습니까? 관련된 빌드 기록은 유지됩니다.')) return;

        try {
            await adminApi.deleteProduct(productId);
            toast.success('제품이 삭제되었습니다.');
            loadProducts();
        } catch (err) {
            toast.error(err.response?.data?.error || '제품 삭제 실패');
        }
    };

    // Profile modal handlers
    const handleOpenProfilesModal = async (product) => {
        setSelectedProduct(product);
        setProfilesModal(true);
        loadProfiles(product.id);
    };

    const loadProfiles = async (productId) => {
        try {
            const { data } = await adminApi.getProductProfiles(productId);
            setProfiles(data.profiles || []);
        } catch (err) {
            toast.error('프로파일 로드 실패');
        }
    };

    const handleAddProfile = async (e) => {
        e.preventDefault();
        if (!selectedProduct) return;

        try {
            await adminApi.createProductProfile(selectedProduct.id, {
                name: newProfileName,
                description: newProfileDesc,
            });
            toast.success('프로파일이 추가되었습니다.');
            setNewProfileName('');
            setNewProfileDesc('');
            loadProfiles(selectedProduct.id);
            loadProducts();
        } catch (err) {
            toast.error(err.response?.data?.error || '프로파일 추가 실패');
        }
    };

    const handleDeleteProfile = async (profileId) => {
        if (!window.confirm('프로파일을 삭제하시겠습니까?')) return;

        try {
            await adminApi.deleteProductProfile(selectedProduct.id, profileId);
            toast.success('프로파일이 삭제되었습니다.');
            loadProfiles(selectedProduct.id);
            loadProducts();
        } catch (err) {
            toast.error(err.response?.data?.error || '프로파일 삭제 실패');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={styles.header}>
                <Typography variant="h2">제품 목록</Typography>
                <Button variant="contained" onClick={handleShowAddForm}>
                    + 제품 추가
                </Button>
            </Box>

            <Typography sx={styles.helpText}>
                제품은 Bundle(단일 저장소) 또는 BackEnd(다중 저장소) 타입으로 구성할 수 있습니다.
            </Typography>

            {showForm && (
                <Paper sx={styles.formContainer}>
                    <Typography variant="h3" sx={{ mb: 2 }}>
                        {editMode ? '제품 수정' : '새 제품 추가'}
                    </Typography>

                    <Box component="form" onSubmit={handleSubmitForm}>
                        {/* 공통 항목 */}
                        <Box sx={styles.formSection}>
                            <Typography sx={styles.sectionTitle}>
                                공통 항목
                            </Typography>
                            <Box sx={styles.formRow}>
                                <TextField
                                    label="제품 ID *"
                                    value={form.id}
                                    onChange={(e) => setForm({ ...form, id: e.target.value })}
                                    required
                                    disabled={editMode}
                                    size="small"
                                    sx={{ flex: 1 }}
                                    helperText="영문 소문자, 숫자, 하이픈만 사용"
                                    inputProps={{ pattern: '[a-z0-9\\-]+' }}
                                />
                                <TextField
                                    label="제품 이름 *"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    required
                                    size="small"
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                            <Box sx={styles.formRow}>
                                <TextField
                                    label="제품 설명"
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    size="small"
                                    sx={{ flex: 2 }}
                                />
                                <TextField
                                    select
                                    label="Node 버전"
                                    value={form.nodeVersion}
                                    onChange={(e) => setForm({ ...form, nodeVersion: e.target.value })}
                                    size="small"
                                    sx={{ flex: 1 }}
                                    SelectProps={{ native: true }}
                                >
                                    <option value="16">Node 16</option>
                                    <option value="18">Node 18</option>
                                    <option value="20">Node 20</option>
                                    <option value="22">Node 22</option>
                                </TextField>
                            </Box>
                        </Box>

                        {/* 옵션 타입 선택 */}
                        <Box sx={styles.formSection}>
                            <Typography sx={styles.sectionTitle}>
                                옵션 타입
                            </Typography>
                            <RadioGroup
                                row
                                value={form.optionType}
                                onChange={(e) => setForm({ ...form, optionType: e.target.value })}
                            >
                                <FormControlLabel value="bundle" control={<Radio size="small" />} label="Bundle (단일 저장소)" />
                                <FormControlLabel value="backend" control={<Radio size="small" />} label="BackEnd (다중 저장소)" />
                            </RadioGroup>
                        </Box>

                        {/* 옵션1: Bundle */}
                        {form.optionType === 'bundle' && (
                            <Box sx={styles.formSection}>
                                <Typography sx={styles.sectionTitle}>
                                    Repository 경로
                                </Typography>
                                <TextField
                                    label="Git URL *"
                                    value={form.repoUrl}
                                    onChange={(e) => setForm({ ...form, repoUrl: e.target.value })}
                                    required
                                    size="small"
                                    fullWidth
                                    placeholder="git@bitbucket.org:org/repo.git"
                                    helperText="단일 저장소 경로를 입력하세요"
                                />
                            </Box>
                        )}

                        {/* 옵션2: BackEnd */}
                        {form.optionType === 'backend' && (
                            <>
                                <Box sx={styles.formSection}>
                                    <Typography sx={styles.sectionTitle}>
                                        BackEnd Repository 경로
                                    </Typography>
                                    <Box sx={styles.formRow}>
                                        <TextField
                                            label="Git URL *"
                                            value={form.backendUrl}
                                            onChange={(e) => setForm({ ...form, backendUrl: e.target.value })}
                                            required
                                            size="small"
                                            sx={{ flex: 2 }}
                                            placeholder="git@bitbucket.org:org/repo.git"
                                        />
                                        <TextField
                                            label="브랜치"
                                            value={form.backendBranch}
                                            onChange={(e) => setForm({ ...form, backendBranch: e.target.value })}
                                            size="small"
                                            sx={{ flex: 1 }}
                                            placeholder="main"
                                        />
                                    </Box>
                                </Box>

                                <Box sx={styles.formSection}>
                                    <Typography sx={styles.sectionTitle}>
                                        FrontEnd Repository 경로
                                    </Typography>
                                    <Box sx={styles.formRow}>
                                        <TextField
                                            label="Git URL"
                                            value={form.frontendUrl}
                                            onChange={(e) => setForm({ ...form, frontendUrl: e.target.value })}
                                            size="small"
                                            sx={{ flex: 2 }}
                                            placeholder="git@bitbucket.org:org/repo.git"
                                        />
                                        <TextField
                                            label="브랜치"
                                            value={form.frontendBranch}
                                            onChange={(e) => setForm({ ...form, frontendBranch: e.target.value })}
                                            size="small"
                                            sx={{ flex: 1 }}
                                            placeholder="main"
                                        />
                                    </Box>
                                </Box>

                                <Box sx={styles.formSection}>
                                    <Typography sx={styles.sectionTitle}>
                                        Convert Repository 경로
                                    </Typography>
                                    <Box sx={styles.formRow}>
                                        <TextField
                                            label="Git URL"
                                            value={form.convertUrl}
                                            onChange={(e) => setForm({ ...form, convertUrl: e.target.value })}
                                            size="small"
                                            sx={{ flex: 2 }}
                                            placeholder="git@bitbucket.org:org/repo.git"
                                        />
                                        <TextField
                                            label="브랜치"
                                            value={form.convertBranch}
                                            onChange={(e) => setForm({ ...form, convertBranch: e.target.value })}
                                            size="small"
                                            sx={{ flex: 1 }}
                                            placeholder="main"
                                        />
                                    </Box>
                                </Box>

                                <Box sx={styles.formSection}>
                                    <Typography sx={styles.sectionTitle}>
                                        빌드 스크립트 설정
                                    </Typography>
                                    <Box sx={styles.formRow}>
                                        <TextField
                                            label="빌드 스크립트 경로 *"
                                            value={form.buildScript}
                                            onChange={(e) => setForm({ ...form, buildScript: e.target.value })}
                                            required
                                            size="small"
                                            sx={{ flex: 2 }}
                                            placeholder="server/build/itsi_v1_0.js"
                                            helperText="node로 실행할 빌드 스크립트 경로"
                                        />
                                    </Box>
                                    {/* 브랜치 모드 - 현재 사용하지 않음 (release 모드 고정)
                                    <Box sx={styles.formRow}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 0.5 }}>
                                                브랜치 모드
                                            </Typography>
                                            <RadioGroup
                                                row
                                                value={form.branchMode}
                                                onChange={(e) => setForm({ ...form, branchMode: e.target.value, fixedBranch: '', majorVersion: '' })}
                                            >
                                                <FormControlLabel value="release" control={<Radio size="small" />} label="릴리즈 (release/* 자동 조회)" />
                                                <FormControlLabel value="fixed" control={<Radio size="small" />} label="고정 (직접 입력)" />
                                            </RadioGroup>
                                        </Box>
                                        {form.branchMode === 'fixed' && (
                                            <TextField
                                                label="고정 브랜치명 *"
                                                value={form.fixedBranch}
                                                onChange={(e) => setForm({ ...form, fixedBranch: e.target.value })}
                                                required
                                                size="small"
                                                sx={{ flex: 1 }}
                                                placeholder="perf/optimization"
                                                helperText="빌드 요청 시 이 브랜치가 자동 선택됩니다"
                                            />
                                        )}
                                        {form.branchMode === 'release' && (
                                            <TextField
                                                label="Major 버전 *"
                                                value={form.majorVersion}
                                                onChange={(e) => setForm({ ...form, majorVersion: e.target.value })}
                                                required
                                                size="small"
                                                type="number"
                                                sx={{ width: 120 }}
                                                placeholder="1"
                                                helperText="예: 1 → release/1.x.x"
                                                inputProps={{ min: 1 }}
                                            />
                                        )}
                                    </Box>
                                    */}
                                </Box>
                            </>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <Button type="submit" variant="contained" disabled={submitting}>
                                저장
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setShowForm(false);
                                    setForm(initialFormState);
                                }}
                            >
                                취소
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            <TableContainer sx={styles.tableContainer}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>제품 이름</TableCell>
                            <TableCell>타입</TableCell>
                            <TableCell>Node</TableCell>
                            <TableCell>저장소 정보</TableCell>
                            <TableCell>프로파일</TableCell>
                            <TableCell>액션</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {products.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    등록된 제품이 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            products.map((product) => {
                                const isBundle = product.optionType === 'bundle';
                                let repoInfo = '';

                                if (isBundle) {
                                    repoInfo = product.repoUrl || '-';
                                } else {
                                    const repos = [];
                                    if (product.repos?.backend) repos.push(`B/E: ${product.repos.backend.branch}`);
                                    if (product.repos?.frontend) repos.push(`F/E: ${product.repos.frontend.branch}`);
                                    if (product.repos?.convert) repos.push(`Conv: ${product.repos.convert.branch}`);
                                    repoInfo = repos.join(', ') || '-';
                                }

                                return (
                                    <TableRow key={product.id} hover>
                                        <TableCell>
                                            <code>{product.id}</code>
                                        </TableCell>
                                        <TableCell>{product.name}</TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '0.8125rem' }}>
                                                {isBundle ? 'Bundle' : 'BackEnd'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{product.nodeVersion || '20'}</TableCell>
                                        <TableCell>
                                            <Typography sx={styles.repoInfo}>
                                                {repoInfo}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleOpenProfilesModal(product)}
                                            >
                                                {(product.profiles || []).length}개
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    size="small"
                                                    onClick={() => handleEditProduct(product.id)}
                                                >
                                                    수정
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteProduct(product.id)}
                                                >
                                                    삭제
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Profiles Modal */}
            <Dialog
                open={profilesModal}
                onClose={() => setProfilesModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    프로파일 관리 - {selectedProduct?.name}
                    <IconButton onClick={() => setProfilesModal(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleAddProfile} sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
                        <TextField
                            label="프로파일 이름"
                            value={newProfileName}
                            onChange={(e) => setNewProfileName(e.target.value)}
                            required
                            size="small"
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            label="설명"
                            value={newProfileDesc}
                            onChange={(e) => setNewProfileDesc(e.target.value)}
                            size="small"
                            sx={{ flex: 1 }}
                        />
                        <Button type="submit" variant="contained">
                            추가
                        </Button>
                    </Box>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>이름</TableCell>
                                <TableCell>설명</TableCell>
                                <TableCell>액션</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {profiles.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        프로파일이 없습니다.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                profiles.map((profile) => (
                                    <TableRow key={profile.id}>
                                        <TableCell>{profile.name}</TableCell>
                                        <TableCell>{profile.description || '-'}</TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteProfile(profile.id)}
                                            >
                                                삭제
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setProfilesModal(false)}>닫기</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
